import { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { ShoppingCart, Plus, Minus, X, Trash2, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const categories = ['All', 'Apparel', 'Yard Signs & Stickers', 'Accessories', 'Branded Items'];

export default function Store() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('All');
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [selectedColors, setSelectedColors] = useState({});
  const { items, addItem, removeItem, updateQuantity, clearCart, total, itemCount } = useCart();
  const { user } = useAuth();
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const filtered = category === 'All' ? products : products.filter(p => p.category === category);

  const handleAddToCart = (product) => {
    const size = selectedSizes[product.id] || (product.sizes?.[0] || '');
    const color = selectedColors[product.id] || (product.colors?.[0] || '');
    addItem(product, size, color);
    toast.success(`${product.name} added to cart`);
  };

  const handlePaymentSuccess = async (details) => {
    try {
      const orderData = {
        items: items.map(i => ({ product_id: i.id, name: i.name, price: i.price, quantity: i.quantity, size: i.size, color: i.color })),
        total, paypal_order_id: details.id,
        customer_name: user?.name || details.payer?.name?.given_name || '',
        customer_email: user?.email || details.payer?.email_address || '',
      };
      const { data } = await axios.post(`${API}/orders`, orderData, { withCredentials: true });
      setOrderDetails(data);
      setOrderComplete(true);
      clearCart();
      setCartOpen(false);
      toast.success('Order placed successfully!');
    } catch {
      toast.error('Order recording failed. Please contact support.');
    }
  };

  if (orderComplete && orderDetails) {
    return (
      <div className="pt-16 min-h-screen bg-[#FAFAFA]" data-testid="order-confirmation">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="bg-white border border-gray-200 rounded-lg p-10">
            <div className="w-16 h-16 mx-auto mb-4 bg-forest/10 rounded-full flex items-center justify-center">
              <ShoppingCart className="w-8 h-8 text-forest" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-navy mb-2">Order Confirmed!</h1>
            <p className="text-gray-600 font-body mb-6">Thank you for supporting Safe Florida.</p>
            <div className="bg-[#FAFAFA] rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-body"><strong>Order Number:</strong> {orderDetails.order_number}</p>
              <p className="text-sm font-body"><strong>Total:</strong> ${orderDetails.total?.toFixed(2)}</p>
              <p className="text-sm font-body"><strong>Status:</strong> {orderDetails.status}</p>
            </div>
            <Button onClick={() => { setOrderComplete(false); setOrderDetails(null); }} className="btn-primary-safe font-body" data-testid="continue-shopping-btn">
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16" data-testid="store-page">
      <section className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Official Merchandise</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">Safe Florida Store</h1>
          <p className="text-lg text-white/80 font-body">Show your support with official Safe Florida merchandise.</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Categories + Cart Toggle */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div className="flex flex-wrap gap-2">
              {categories.map(c => (
                <button key={c} onClick={() => setCategory(c)}
                  className={`px-4 py-2 text-sm font-body rounded-md transition-colors ${category === c ? 'bg-navy text-white' : 'bg-gray-100 text-gray-700 hover:bg-navy/10'}`}
                  data-testid={`category-${c.toLowerCase().replace(/\s+/g, '-')}`}>
                  {c}
                </button>
              ))}
            </div>
            <Button onClick={() => setCartOpen(!cartOpen)} variant="outline" className="border-navy text-navy font-body relative" data-testid="toggle-cart-btn">
              <ShoppingCart className="w-4 h-4 mr-2" /> Cart
              {itemCount > 0 && <Badge className="ml-2 bg-gold text-navy">{itemCount}</Badge>}
            </Button>
          </div>

          <div className="flex gap-8">
            {/* Product Grid */}
            <div className={`flex-1 grid ${cartOpen ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-6`}>
              {filtered.map(product => (
                <div key={product.id} className="border border-gray-200 rounded-lg overflow-hidden card-hover" data-testid={`product-card-${product.id}`}>
                  <div className="h-48 bg-navy/5 flex items-center justify-center overflow-hidden">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <Tag className="w-10 h-10 text-navy/30 mx-auto mb-2" />
                        <p className="text-xs text-navy/50 font-body">Safe Florida</p>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-heading font-bold text-navy text-sm">{product.name}</h3>
                      <span className="font-body font-bold text-gold text-lg">${product.price}</span>
                    </div>
                    <p className="text-xs text-gray-600 font-body mb-3 line-clamp-2">{product.description}</p>
                    {product.sizes?.length > 1 && (
                      <div className="mb-2">
                        <label className="text-xs font-body text-gray-500 block mb-1">Size</label>
                        <div className="flex flex-wrap gap-1">
                          {product.sizes.map(s => (
                            <button key={s} onClick={() => setSelectedSizes(p => ({ ...p, [product.id]: s }))}
                              className={`px-2 py-0.5 text-xs rounded border font-body ${(selectedSizes[product.id] || product.sizes[0]) === s ? 'border-navy bg-navy text-white' : 'border-gray-300 text-gray-600'}`}>
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {product.colors?.length > 1 && (
                      <div className="mb-3">
                        <label className="text-xs font-body text-gray-500 block mb-1">Color</label>
                        <div className="flex flex-wrap gap-1">
                          {product.colors.map(c => (
                            <button key={c} onClick={() => setSelectedColors(p => ({ ...p, [product.id]: c }))}
                              className={`px-2 py-0.5 text-xs rounded border font-body ${(selectedColors[product.id] || product.colors[0]) === c ? 'border-navy bg-navy text-white' : 'border-gray-300 text-gray-600'}`}>
                              {c}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <Button onClick={() => handleAddToCart(product)} className="w-full btn-primary-safe font-body text-xs" data-testid={`add-to-cart-${product.id}`}>
                      Add to Cart
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Sidebar */}
            {cartOpen && (
              <div className="w-80 flex-shrink-0 hidden md:block" data-testid="cart-sidebar">
                <div className="border border-gray-200 rounded-lg p-4 sticky top-20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-bold text-navy">Shopping Cart</h3>
                    <button onClick={() => setCartOpen(false)}><X className="w-4 h-4 text-gray-500" /></button>
                  </div>
                  {items.length === 0 ? (
                    <p className="text-sm text-gray-500 font-body text-center py-8">Your cart is empty</p>
                  ) : (
                    <>
                      <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                        {items.map(item => (
                          <div key={item.key} className="flex items-center gap-3 border-b border-gray-100 pb-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-body font-medium text-navy truncate">{item.name}</p>
                              <p className="text-xs text-gray-500 font-body">{[item.size, item.color].filter(Boolean).join(' / ')}</p>
                              <p className="text-sm font-body font-bold text-gold">${(item.price * item.quantity).toFixed(2)}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={() => updateQuantity(item.key, item.quantity - 1)} className="w-6 h-6 flex items-center justify-center border rounded text-xs"><Minus className="w-3 h-3" /></button>
                              <span className="text-sm font-body w-6 text-center">{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.key, item.quantity + 1)} className="w-6 h-6 flex items-center justify-center border rounded text-xs"><Plus className="w-3 h-3" /></button>
                            </div>
                            <button onClick={() => removeItem(item.key)}><Trash2 className="w-4 h-4 text-red-400" /></button>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-gray-200 pt-4 mb-4">
                        <div className="flex justify-between mb-1">
                          <span className="font-body text-sm text-gray-600">Subtotal</span>
                          <span className="font-body font-bold text-navy">${total.toFixed(2)}</span>
                        </div>
                      </div>
                      <div data-testid="paypal-store-checkout">
                        <PayPalButtons
                          style={{ layout: 'vertical', shape: 'rect' }}
                          createOrder={(data, actions) => actions.order.create({
                            purchase_units: [{ amount: { value: total.toFixed(2) }, description: 'Safe Florida Store Purchase' }]
                          })}
                          onApprove={(data, actions) => actions.order.capture().then(handlePaymentSuccess)}
                          onError={() => toast.error('Payment failed.')}
                        />
                      </div>
                      <button onClick={clearCart} className="w-full text-center text-xs text-red-500 font-body mt-2 hover:underline" data-testid="clear-cart-btn">Clear Cart</button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
